import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import api from '@/api/api';
import storageManager from '@/api/storageManager';
import { getUserProfileData } from '@/api/query/customer';
import AuthModal from '@/common/components/auth/AuthModal';
import PlanCheckoutModal from '@/common/components/paywall/PlanCheckoutModal';
import { syncCustomerLanguageWithHost } from '@/common/utils/syncCustomerLanguage';

const GuestAuthContext = createContext(null);

const LOGOUT_DELAY_MS = 2000;

export const useGuestAuth = () => {
	const ctx = useContext(GuestAuthContext);
	if (!ctx) {
		throw new Error('useGuestAuth must be used within GuestAuthProvider');
	}
	return ctx;
};

export const isAuthenticated = () => Boolean(storageManager.auth.getToken());

export const GuestAuthProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [isAuthLoading, setIsAuthLoading] = useState(
		Boolean(storageManager.auth.getToken()),
	);
	const [authModalOpen, setAuthModalOpen] = useState(false);
	const [authModalTab, setAuthModalTab] = useState('register');
	const [authReturnPath, setAuthReturnPath] = useState(null);
	const [authRegistrationSource, setAuthRegistrationSource] = useState(null);
	const [authPrefilledEmail, setAuthPrefilledEmail] = useState(null);
	const [authAutoAdvanceEmail, setAuthAutoAdvanceEmail] = useState(false);
	const [planPaywallOpen, setPlanPaywallOpen] = useState(false);
	const [planPaywallReturnPath, setPlanPaywallReturnPath] = useState('/studio');
	const [authRevision, setAuthRevision] = useState(0);
	const [isLoggingOut, setIsLoggingOut] = useState(false);
	const logoutTimeoutRef = useRef(null);

	const loadUser = useCallback(async () => {
		const token = storageManager.auth.getToken();

		if (!token) {
			setUser(null);
			setIsAuthLoading(false);
			return null;
		}

		setIsAuthLoading(true);

		try {
			const { data } = await getUserProfileData();
			setUser(data);
			void syncCustomerLanguageWithHost().catch(() => undefined);
			return data;
		} catch (error) {
			console.error('Failed to load user profile:', error);
			const status = error?.response?.status;

			if (status === 401 || status === 403) {
				storageManager.auth.clearAuth(api.axios);
				setAuthRevision((current) => current + 1);
			}

			setUser(null);
			return null;
		} finally {
			setIsAuthLoading(false);
		}
	}, []);

	useEffect(() => {
		void loadUser();
	}, [loadUser]);

	useEffect(() => {
		return () => {
			clearTimeout(logoutTimeoutRef.current);
		};
	}, []);

	const refreshAuthState = useCallback(async () => {
		setAuthRevision((current) => current + 1);
		return loadUser();
	}, [loadUser]);

	const markSessionAuthenticated = useCallback(() => {
		void refreshAuthState();
	}, [refreshAuthState]);

	const updateUser = useCallback((partial) => {
		setUser((current) => (current ? { ...current, ...partial } : current));
	}, []);

	const logout = useCallback(
		(onComplete) => {
			if (isLoggingOut) {
				return;
			}

			setIsLoggingOut(true);

			logoutTimeoutRef.current = setTimeout(() => {
				storageManager.auth.clearAuth(api.axios);
				setUser(null);
				refreshAuthState();
				setIsLoggingOut(false);
				onComplete?.();
			}, LOGOUT_DELAY_MS);
		},
		[isLoggingOut, refreshAuthState],
	);

	const isLoggedIn = useMemo(
		() => Boolean(storageManager.auth.getToken()),
		[authRevision],
	);
	const guest = !isLoggedIn;

	const openAuthModal = useCallback(
		(
			tab = 'register',
			returnPath = null,
			registrationSource = null,
			options = {},
		) => {
			const resolvedReturnPath =
				returnPath ?? `${window.location.pathname}${window.location.search}`;

			setAuthModalTab(tab);
			setAuthReturnPath(resolvedReturnPath);
			setAuthRegistrationSource(registrationSource);
			setAuthPrefilledEmail(options.prefilledEmail ?? null);
			setAuthAutoAdvanceEmail(Boolean(options.autoAdvanceEmail));
			setAuthModalOpen(true);
		},
		[],
	);

	const closeAuthModal = useCallback(() => {
		setAuthModalOpen(false);
		setAuthReturnPath(null);
		setAuthRegistrationSource(null);
		setAuthPrefilledEmail(null);
		setAuthAutoAdvanceEmail(false);
	}, []);

	const clearAuthReturnPath = useCallback(() => {
		setAuthReturnPath(null);
	}, []);

	const openPlanPaywall = useCallback((returnPath = '/studio') => {
		setPlanPaywallReturnPath(returnPath || '/studio');
		setPlanPaywallOpen(true);
	}, []);

	const closePlanPaywall = useCallback(() => {
		setPlanPaywallOpen(false);
	}, []);

	const requireAuth = useCallback(
		(action) => {
			if (isAuthenticated()) {
				return action();
			}
			openAuthModal('register');
			return false;
		},
		[openAuthModal],
	);

	const value = useMemo(
		() => ({
			user,
			isAuthLoading,
			isGuest: guest,
			isLoggedIn,
			authModalOpen,
			authModalTab,
			authReturnPath,
			authRegistrationSource,
			authPrefilledEmail,
			authAutoAdvanceEmail,
			planPaywallOpen,
			planPaywallReturnPath,
			openAuthModal,
			closeAuthModal,
			clearAuthReturnPath,
			openPlanPaywall,
			closePlanPaywall,
			requireAuth,
			logout,
			isLoggingOut,
			refreshAuthState,
			markSessionAuthenticated,
			updateUser,
		}),
		[
			user,
			isAuthLoading,
			guest,
			isLoggedIn,
			authModalOpen,
			authModalTab,
			authReturnPath,
			authRegistrationSource,
			authPrefilledEmail,
			authAutoAdvanceEmail,
			planPaywallOpen,
			planPaywallReturnPath,
			openAuthModal,
			closeAuthModal,
			clearAuthReturnPath,
			openPlanPaywall,
			closePlanPaywall,
			requireAuth,
			logout,
			isLoggingOut,
			refreshAuthState,
			markSessionAuthenticated,
			updateUser,
		],
	);

	return (
		<GuestAuthContext.Provider value={value}>
			{children}
			{authModalOpen && (
				<AuthModal tab={authModalTab} onClose={closeAuthModal} />
			)}
			{planPaywallOpen && (
				<PlanCheckoutModal
					secured
					returnPath={planPaywallReturnPath}
					onClose={closePlanPaywall}
				/>
			)}
		</GuestAuthContext.Provider>
	);
};
